import {
  Group, Mesh, BoxGeometry, CylinderGeometry, ConeGeometry,
  MeshLambertMaterial, MeshPhongMaterial, Scene, Vector3, Object3D,
} from 'three';
import { IEnemy } from './IEnemy';

const ATTACK_RANGE    = 1400;
const ATTACK_COOLDOWN = 2.5;
const DAMAGE          = 12;
const MISSILE_RANGE   = 2500;
const MISSILE_COOLDOWN = 16.0;
const LOCK_TIME       = 1.5;

/**
 * 対空砲台モデル（SAMランチャー風）
 * 砲台部分は別グループで旋回する
 */
function buildTurretModel(): { root: Group; turretHead: Object3D } {
  const root = new Group();

  const matGreen  = new MeshLambertMaterial({ color: 0x4a5a3a });
  const matDark   = new MeshLambertMaterial({ color: 0x2a3a2a });
  const matSteel  = new MeshLambertMaterial({ color: 0x555550 });
  const matBarrel = new MeshPhongMaterial({ color: 0x333333, shininess: 60 });

  // ── 台座（車両ボディ） ─────────────────────────────
  const chassis = new Mesh(new BoxGeometry(8, 2.0, 12), matGreen);
  chassis.position.set(0, 1.0, 0);
  root.add(chassis);

  // 車両前部パネル
  const frontPanel = new Mesh(new BoxGeometry(8, 2.2, 0.5), matDark);
  frontPanel.position.set(0, 1.0, -6);
  root.add(frontPanel);

  // キャタピラ（左右）
  for (const sx of [-1, 1]) {
    const track = new Mesh(new BoxGeometry(1.8, 1.5, 12), matDark);
    track.position.set(sx * 4.5, 0.75, 0);
    root.add(track);
    // ホイール
    for (let zi = -4; zi <= 4; zi += 2) {
      const wheel = new Mesh(new CylinderGeometry(0.7, 0.7, 1.5, 8), matSteel);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(sx * 4.6, 0.7, zi * 1.2);
      root.add(wheel);
    }
  }

  // ── 旋回砲塔ベース ────────────────────────────────
  const turretBase = new Mesh(new CylinderGeometry(2.8, 3.2, 1.2, 10), matGreen);
  turretBase.position.set(0, 2.6, 0);
  root.add(turretBase);

  // 砲塔ヘッド（旋回する部分）
  const turretHead = new Group();
  turretHead.position.set(0, 3.5, 0);
  root.add(turretHead);

  // 砲塔本体
  const turretBody = new Mesh(new BoxGeometry(4.5, 2.0, 5.5), matGreen);
  turretBody.position.set(0, 0, 0);
  turretHead.add(turretBody);

  // 砲身（連装）
  for (const sx of [-0.7, 0.7]) {
    const barrelBase = new Mesh(new CylinderGeometry(0.38, 0.38, 5.5, 8), matBarrel);
    barrelBase.rotation.x = Math.PI / 2;
    barrelBase.position.set(sx, 0.3, -4.2);
    turretHead.add(barrelBase);

    const muzzle = new Mesh(new CylinderGeometry(0.32, 0.4, 0.8, 8), matBarrel);
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.set(sx, 0.3, -7.1);
    turretHead.add(muzzle);

    const muzzleBrake = new Mesh(new BoxGeometry(0.9, 0.5, 0.4), matBarrel);
    muzzleBrake.position.set(sx, 0.3, -7.6);
    turretHead.add(muzzleBrake);
  }

  // レーダーアンテナ
  const radar = new Mesh(new BoxGeometry(2.0, 0.12, 1.2), matDark);
  radar.position.set(0, 1.3, 1.0);
  turretHead.add(radar);
  const radarPole = new Mesh(new CylinderGeometry(0.1, 0.1, 0.8, 6), matSteel);
  radarPole.position.set(0, 0.9, 1.0);
  turretHead.add(radarPole);

  return { root, turretHead };
}

export class Turret implements IEnemy {
  readonly group: Group;
  private turretHead: Object3D;
  private hp = 80;
  private attackTimer = 0;
  isAlive = true;
  hitRadius = 10;
  readonly isGroundUnit = true;

  /** EnemyManagerが設定する弾丸発射コールバック */
  onFireBullet: ((from: Vector3, dir: Vector3) => void) | null = null;
  /** EnemyManagerが設定するミサイル発射コールバック */
  onFireMissile: ((from: Vector3, dir: Vector3) => void) | null = null;

  /** ロックオン状態 */
  lockingPlayer = false;
  lockTimer = 0;
  private missileTimer = 0;

  constructor(private scene: Scene, pos: [number, number, number]) {
    const { root, turretHead } = buildTurretModel();
    this.group = root;
    this.turretHead = turretHead;
    this.group.position.set(pos[0], pos[1], pos[2]);
    scene.add(this.group);
  }

  get position(): Vector3 { return this.group.position; }

  takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.isAlive = false;
      this.scene.remove(this.group);
    }
    return !this.isAlive;
  }

  /** @returns ダメージ量 */
  update(delta: number, playerPos: Vector3): number {
    if (!this.isAlive) return 0;

    this.attackTimer = Math.max(0, this.attackTimer - delta);
    this.missileTimer = Math.max(0, this.missileTimer - delta);
    const dist = this.group.position.distanceTo(playerPos);

    // ── ロックオン更新 ──
    if (dist < MISSILE_RANGE) {
      this.lockTimer = Math.min(this.lockTimer + delta, LOCK_TIME);
    } else {
      this.lockTimer = Math.max(0, this.lockTimer - delta * 2);
    }
    this.lockingPlayer = this.lockTimer >= LOCK_TIME;

    // ── ミサイル発射 ──
    if (this.lockingPlayer && this.missileTimer <= 0 && this.onFireMissile) {
      this.missileTimer = MISSILE_COOLDOWN;
      const dir = playerPos.clone().sub(this.group.position).normalize();
      const from = this.group.position.clone().add(new Vector3(0, 5, 0));
      this.onFireMissile(from, dir);
    }

    if (dist < ATTACK_RANGE) {
      // 砲塔ヘッドをプレイヤー方向に旋回
      const worldPos = this.group.position.clone();
      const targetLocal = playerPos.clone().sub(worldPos);
      this.turretHead.lookAt(worldPos.clone().add(targetLocal));

      if (this.attackTimer <= 0) {
        this.attackTimer = ATTACK_COOLDOWN;

        // 弾丸発射
        if (this.onFireBullet) {
          const dir = playerPos.clone().sub(this.group.position).normalize();
          const from = this.group.position.clone().add(
            new Vector3(0, 4.5, 0).add(dir.clone().multiplyScalar(3))
          );
          this.onFireBullet(from, dir);
        }

        const dmg = dist < 400 ? DAMAGE : Math.round(DAMAGE * (1 - dist / ATTACK_RANGE));
        return Math.max(1, dmg);
      }
    }
    return 0;
  }
}
